import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';
import TankChart from './components/TankChart';
import HaulTable from './components/HaulTable';
import SummaryStats from './components/SummaryStats';
import DataPage from './components/DataPage';
import ErrorPlot from './components/ErrorPlot';
import VolumeDistribution from './components/VolumeDistribution';
import LeakDetector from './components/LeakDetector';
import FleetLeaks from './components/FleetLeaks';
import AllWellsPage from './components/AllWellsPage';
import WaterRatePage from './components/WaterRatePage';
import useDeactivation, { haulKey } from './useDeactivation';
import {
  getWellIndex, getWell, getWellRecent, getStatus,
  triggerRefresh, invalidateCache, setTokenGetter, setAuthExpiredHandler,
} from './lib/dataSource';
import { useAuth } from './auth/AuthProvider';

function App() {
  const { user, roles, isAdmin, logout, getAccessToken, forceReLogin, authEnabled } = useAuth();

  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  useEffect(() => {
    setAuthExpiredHandler(forceReLogin);
  }, [forceReLogin]);

  const [wellList, setWellList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [wellLoading, setWellLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState(null);
  const [wellData, setWellData] = useState(null);
  const [activeTank, setActiveTank] = useState('tank1');
  const [search, setSearch] = useState('');
  const [minHaulFt, setMinHaulFt] = useState(3);
  const [tankDiaFt, setTankDiaFt] = useState(12);
  const [numTanks, setNumTanks] = useState(2);
  const [highlightedHaul, setHighlightedHaul] = useState(null);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [activePage, setActivePage] = useState('chart');
  const [showDeactivated, setShowDeactivated] = useState(false);
  const deact = useDeactivation();

  const [refreshing, setRefreshing] = useState(false);
  const [dataStatus, setDataStatus] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    getWellIndex()
      .then(d => {
        setWellList(d.wellList);
        if (d.wellList.length > 0) {
          setSelectedWellId(String(d.wellList[0].deviceId));
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
    getStatus().then(setDataStatus).catch(() => {});
  }, []);

  // Force the server to pull live data from Databricks and recompute, then
  // reload the currently-viewed data. Historic data already held is not
  // re-pulled; only new rows are fetched (server-side), and only changed files
  // are re-downloaded (ETag/304).
  const [refreshMsg, setRefreshMsg] = useState(null);
  const handleForceRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    setRefreshMsg(null);
    try {
      await triggerRefresh(false);           // check Databricks for new rows
      invalidateCache();                      // allow changed files to re-load
      const idx = await getWellIndex({ force: true });
      setWellList(idx.wellList);
      setReloadNonce(n => n + 1);             // re-trigger well load
      setDataStatus(await getStatus());
    } catch (e) {
      // 428 => auth not set up; surface the server's actionable guidance.
      const detail = e && e.authRequired ? e.detail : (e && e.message) || 'Refresh failed';
      setRefreshMsg(detail);
      console.error('refresh failed', detail);
    } finally {
      setRefreshing(false);
    }
  }, [refreshing]);

  const [fullLoaded, setFullLoaded] = useState(false);
  const loadRef = useRef({ id: null, fullArrived: false });

  useEffect(() => {
    if (!selectedWellId) return;

    const thisLoad = { id: selectedWellId, fullArrived: false };
    loadRef.current = thisLoad;

    setWellLoading(true);
    setWellData(null);
    setFullLoaded(false);
    setHighlightedHaul(null);

    const initTankAndRange = (d) => {
      const tanks = d.tanks || {};
      const firstTank = tanks.tank1 ? 'tank1' : Object.keys(tanks)[0];
      setActiveTank(firstTank || 'tank1');
      const ts = tanks[firstTank]?.timeSeries;
      if (ts && ts.length > 0) {
        const lastDate = ts[ts.length - 1].time.slice(0, 10);
        const sevenDaysBack = new Date(new Date(lastDate) - 7 * 86400000).toISOString().slice(0, 10);
        setDateRange({ start: sevenDaysBack, end: lastDate });
      }
    };

    getWellRecent(selectedWellId)
      .then(d => {
        if (loadRef.current !== thisLoad || thisLoad.fullArrived) return;
        setWellData(d);
        setWellLoading(false);
        initTankAndRange(d);
      })
      .catch(() => {});

    getWell(selectedWellId)
      .then(d => {
        if (loadRef.current !== thisLoad) return;
        thisLoad.fullArrived = true;
        setWellData(d);
        setFullLoaded(true);
        setWellLoading(false);
        initTankAndRange(d);
      })
      .catch(() => {
        if (loadRef.current === thisLoad) setWellLoading(false);
      });
  }, [selectedWellId, reloadNonce]);

  const filteredWells = useMemo(() => {
    const q = search.toLowerCase();
    return wellList.filter(w => w.wellName.toLowerCase().includes(q));
  }, [wellList, search]);

  const tankData = useMemo(() => {
    if (!wellData) return null;
    return wellData.tanks?.[activeTank] || null;
  }, [wellData, activeTank]);

  const { filteredTs, filteredHauls } = useMemo(() => {
    if (!tankData) return { filteredTs: [], filteredHauls: [] };

    let ts = tankData.timeSeries;
    let hauls = tankData.hauls.filter(h => h.haul_volume_ft >= minHaulFt);

    // Mark deactivated hauls (bad readings) and drop them unless the user
    // chooses to show them.
    hauls = hauls.map(h => ({
      ...h,
      _deactivated: deact.isHaulOff(haulKey(selectedWellId, activeTank, h)),
    }));
    if (!showDeactivated) {
      hauls = hauls.filter(h => !h._deactivated);
    }

    if (dateRange.start && dateRange.end) {
      const startMs = new Date(dateRange.start).getTime();
      const endMs = new Date(dateRange.end + 'T23:59:59').getTime();
      ts = ts.filter(pt => {
        const t = new Date(pt.time).getTime();
        return t >= startMs && t <= endMs;
      });
      hauls = hauls.filter(h => {
        const t = new Date(h.drop_start).getTime();
        return t >= startMs && t <= endMs;
      });
    }

    return { filteredTs: ts, filteredHauls: hauls };
  }, [tankData, minHaulFt, dateRange, deact, selectedWellId, activeTank, showDeactivated]);

  const filteredStats = useMemo(() => {
    const activeHauls = filteredHauls.filter(h => !h._deactivated);
    if (activeHauls.length === 0) return null;
    const vols = activeHauls.map(h => h.haul_volume_ft);
    vols.sort((a, b) => a - b);
    return {
      totalHauls: vols.length,
      avgHaulFt: vols.reduce((a, b) => a + b, 0) / vols.length,
      medianHaulFt: vols[Math.floor(vols.length / 2)],
      minHaulFt: vols[0],
      maxHaulFt: vols[vols.length - 1],
      totalHauledFt: vols.reduce((a, b) => a + b, 0),
    };
  }, [filteredHauls]);

  const availableTanks = useMemo(() => {
    if (!wellData) return [];
    return Object.keys(wellData.tanks || {});
  }, [wellData]);

  const dataDateRange = useMemo(() => {
    if (!tankData || !tankData.timeSeries.length) return null;
    return {
      min: tankData.timeSeries[0].time.slice(0, 10),
      max: tankData.timeSeries[tankData.timeSeries.length - 1].time.slice(0, 10),
    };
  }, [tankData]);

  const bblPerIn = useMemo(() => {
    if (tankDiaFt <= 0) return 0;
    const radiusFt = tankDiaFt / 2;
    const areaFt2 = Math.PI * radiusFt * radiusFt;
    const cuFtPerIn = areaFt2 / 12;
    return numTanks * cuFtPerIn / 5.6146;
  }, [tankDiaFt, numTanks]);

  const waterRateStats = useMemo(() => {
    if (bblPerIn <= 0) return null;
    const activeHauls = filteredHauls.filter(h => !h._deactivated);
    const valid = activeHauls.filter(h => h.post_slope_ft_per_hr > 0);
    if (valid.length === 0) return null;
    const rates = valid.map(h => h.post_slope_ft_per_hr * bblPerIn * 24);
    const avgWaterRate = rates.reduce((a, b) => a + b, 0) / rates.length;
    const gasValid = valid.filter(h => h.gas_rate_mcf_day > 0);
    const avgGasRate = gasValid.length > 0
      ? gasValid.reduce((s, h) => s + h.gas_rate_mcf_day, 0) / gasValid.length : null;
    const wgrValid = valid.filter(h => h.gas_rate_mcf_day > 0);
    const avgWgr = wgrValid.length > 0
      ? wgrValid.reduce((s, h) => s + (h.post_slope_ft_per_hr * bblPerIn * 24) / h.gas_rate_mcf_day, 0) / wgrValid.length
      : null;
    return { avgWaterRate, avgGasRate, avgWgr, count: valid.length };
  }, [filteredHauls, bblPerIn]);

  const handleSelectWell = useCallback((deviceId) => {
    setSelectedWellId(String(deviceId));
    // If we were on a fleet-wide view, drop into the well's chart.
    setActivePage(p => (p === 'allwells' || p === 'fleet' ? 'chart' : p));
  }, []);

  const setQuickRange = useCallback((days) => {
    if (!dataDateRange) return;
    const end = dataDateRange.max;
    const start = new Date(new Date(end) - days * 86400000).toISOString().slice(0, 10);
    setDateRange({ start: start < dataDateRange.min ? dataDateRange.min : start, end });
    setHighlightedHaul(null);
  }, [dataDateRange]);

  const setFullRange = useCallback(() => {
    if (!dataDateRange) return;
    setDateRange({ start: dataDateRange.min, end: dataDateRange.max });
    setHighlightedHaul(null);
  }, [dataDateRange]);

  // Jump from the Ticket Errors view to the Chart, focused on a specific haul.
  const reviewHaulOnChart = useCallback((haul) => {
    if (!haul) return;
    setActivePage('chart');
    const target = new Date(haul.drop_start).getTime();

    // Ensure the haul is inside the visible date range (widen if needed).
    setDateRange(r => {
      const day = haul.drop_start.slice(0, 10);
      const startMs = r.start ? new Date(r.start).getTime() : Infinity;
      const endMs = r.end ? new Date(r.end + 'T23:59:59').getTime() : -Infinity;
      if (target < startMs || target > endMs) {
        // Center a ±2 day window around the haul for a quick review.
        const pad = 2 * 86400000;
        const start = new Date(target - pad).toISOString().slice(0, 10);
        const end = new Date(target + pad).toISOString().slice(0, 10);
        return { start, end };
      }
      return r;
    });

    // Defer highlight until after filteredHauls reflects the new range.
    setPendingReviewTs(target);
  }, []);

  const [pendingReviewTs, setPendingReviewTs] = useState(null);

  // Once filteredHauls updates for the target range, resolve the index and highlight.
  useEffect(() => {
    if (pendingReviewTs == null) return;
    const idx = filteredHauls.findIndex(
      h => new Date(h.drop_start).getTime() === pendingReviewTs
    );
    if (idx >= 0) {
      setHighlightedHaul(idx);
      setPendingReviewTs(null);
    }
  }, [pendingReviewTs, filteredHauls]);

  if (loading) {
    return <div className="loading"><div className="loading-spinner" />Loading well index...</div>;
  }

  return (
    <div className="app">
      <div className="sidebar">
        <div className="sidebar-header">
          <h1>Water Haul Tracker</h1>
          {authEnabled && user && (
            <div className="user-bar">
              <span className="user-name" title={`Roles: ${roles.join(', ') || 'None'}`}>
                {user.name}
              </span>
              <span className="user-role">{isAdmin ? 'PC-Admin' : 'PC-User'}</span>
              <button className="logout-btn" onClick={logout}>Sign out</button>
            </div>
          )}
          <input
            className="search-input"
            type="text"
            placeholder="Search wells..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <div className="data-refresh">
            {isAdmin && <button
              className="refresh-btn"
              onClick={handleForceRefresh}
              disabled={refreshing}
              title={dataStatus
                ? `Live from Databricks • ${dataStatus.rowCount?.toLocaleString?.() || dataStatus.rowCount} rows\nLatest data: ${dataStatus.maxDatetime || '—'}\nAuto-refresh every ${dataStatus.refreshIntervalMin} min`
                : 'Pull latest data from Databricks'}
            >
              {refreshing ? '⟳ Refreshing…' : '⟳ Refresh data'}
            </button>}
            {dataStatus && dataStatus.maxDatetime && (
              <div className="data-status" title="Timestamp of the most recent data point held locally">
                Data through {String(dataStatus.maxDatetime).slice(0, 16)}
              </div>
            )}
            {dataStatus && dataStatus.authOk === false && (
              <div className="data-authwarn" title={dataStatus.authDetail || ''}>
                ⚠ Live updates unavailable — sign in to Databricks (see SETUP.md)
              </div>
            )}
            {refreshMsg && (
              <div className="data-authwarn">{refreshMsg}</div>
            )}
          </div>
        </div>

        <div className="sidebar-content">
        <div className="fleet-nav">
          <button
            className={activePage === 'allwells' ? 'active' : ''}
            onClick={() => setActivePage('allwells')}
          >
            All Wells
          </button>
          <button
            className={activePage === 'fleet' ? 'active' : ''}
            onClick={() => setActivePage('fleet')}
          >
            Fleet Leaks
          </button>
        </div>

        <div className="well-list">
          {wellList.length === 0 && !loading && (
            <div className="well-list-empty">
              <div className="loading-spinner small" />
              <span>Loading data from Databricks... This may take a few minutes on first launch.</span>
            </div>
          )}
          {filteredWells.map(w => {
            const off = deact.isWellOff(w.deviceId);
            return (
              <div
                key={w.deviceId}
                className={`well-item ${String(w.deviceId) === selectedWellId ? 'active' : ''} ${off ? 'deactivated' : ''}`}
                onClick={() => handleSelectWell(w.deviceId)}
                title={off ? 'Deactivated — excluded from fleet analysis' : ''}
              >
                <span className="name">{w.wellName}</span>
                <span className="count">{w.totalHauls}</span>
                <button
                  className={`deact-btn ${off ? 'on' : ''}`}
                  title={off ? 'Reactivate this well' : 'Deactivate this well (exclude from analysis)'}
                  onClick={(e) => { e.stopPropagation(); deact.toggleWell(w.deviceId); }}
                >
                  {off ? 'Off' : '✕'}
                </button>
              </div>
            );
          })}
        </div>

        <div className="filter-section">
          <label>Min haul size (in)</label>
          <input
            type="range"
            min={1}
            max={30}
            step={0.5}
            value={minHaulFt}
            onChange={e => setMinHaulFt(Number(e.target.value))}
          />
          <div className="filter-value">
            {minHaulFt} in{bblPerIn > 0 ? ` (${(minHaulFt * bblPerIn).toFixed(1)} bbl)` : ''}
          </div>
        </div>

        <div className="tank-config">
          <div className="tank-config-title">Tank Geometry</div>
          <table className="tank-spec-table">
            <thead>
              <tr>
                <th>Tank</th>
                <th>Diameter (ft)</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Tank 1</td>
                <td>{tankDiaFt}</td>
              </tr>
              <tr>
                <td>Tank 2</td>
                <td>{tankDiaFt}</td>
              </tr>
            </tbody>
          </table>
          <div className="tank-config-inputs">
            <label>Diameter (ft)</label>
            <input
              type="number"
              min={1}
              max={30}
              step={0.5}
              value={tankDiaFt}
              onChange={e => setTankDiaFt(Number(e.target.value) || 0)}
            />
          </div>
          <div className="tank-config-inputs">
            <label>Number of tanks</label>
            <input
              type="number"
              min={1}
              max={10}
              step={1}
              value={numTanks}
              onChange={e => setNumTanks(Math.max(1, Math.round(Number(e.target.value) || 1)))}
            />
          </div>
          <div className="tank-config-result">
            <span className="tank-config-value">{bblPerIn.toFixed(2)}</span> bbl per inch of level drop
          </div>
          <div className="helper">
            {numTanks} × π × ({tankDiaFt}/2)² ÷ 12 ÷ 5.6146
          </div>
        </div>
        </div>
      </div>

      <div className="main">
        {activePage === 'allwells' ? (
          <AllWellsPage
            wellList={wellList}
            bblPerIn={bblPerIn}
            minHaulFt={minHaulFt}
            deact={deact}
          />
        ) : activePage === 'fleet' ? (
          <FleetLeaks
            onSelectWell={(id) => { setSelectedWellId(id); setActivePage('leaks'); }}
            deact={deact}
          />
        ) : (
        <>
        {wellLoading && <div className="loading"><div className="loading-spinner" />Loading well data...</div>}
        {!wellLoading && wellData && (
          <>
            <SummaryStats
              wellName={wellData.wellName}
              stats={filteredStats}
              bblPerIn={bblPerIn}
              numHaulsRaw={tankData ? tankData.stats.totalHauls : 0}
              waterRateStats={waterRateStats}
            />
            {wellData._partial && !fullLoaded && (
              <div className="partial-banner">
                <div className="loading-spinner small" />
                Loading full history...
              </div>
            )}

            <div className="controls-row">
              <div className="page-toggle">
                <button
                  className={activePage === 'chart' ? 'active' : ''}
                  onClick={() => setActivePage('chart')}
                >
                  Chart
                </button>
                <button
                  className={activePage === 'data' ? 'active' : ''}
                  onClick={() => setActivePage('data')}
                >
                  Data
                </button>
                <button
                  className={activePage === 'errors' ? 'active' : ''}
                  onClick={() => setActivePage('errors')}
                >
                  Ticket Errors
                </button>
                <button
                  className={activePage === 'voldist' ? 'active' : ''}
                  onClick={() => setActivePage('voldist')}
                >
                  Volume Dist
                </button>
                <button
                  className={activePage === 'leaks' ? 'active' : ''}
                  onClick={() => setActivePage('leaks')}
                >
                  Leak Events
                </button>
                <button
                  className={activePage === 'waterrate' ? 'active' : ''}
                  onClick={() => setActivePage('waterrate')}
                >
                  Water Rate
                </button>
              </div>

              {availableTanks.length > 1 && (
                <div className="tank-toggle">
                  {availableTanks.map(t => (
                    <button
                      key={t}
                      className={t === activeTank ? 'active' : ''}
                      onClick={() => { setActiveTank(t); setHighlightedHaul(null); }}
                    >
                      {t === 'tank1' ? 'Tank 1' : 'Tank 2'}
                    </button>
                  ))}
                </div>
              )}

              <div className="date-controls">
                <button className="quick-btn" onClick={() => setQuickRange(3)}>3d</button>
                <button className="quick-btn" onClick={() => setQuickRange(7)}>7d</button>
                <button className="quick-btn" onClick={() => setQuickRange(14)}>14d</button>
                <button className="quick-btn" onClick={() => setQuickRange(30)}>30d</button>
                <button className="quick-btn" onClick={setFullRange}>All</button>
                <input
                  type="date"
                  className="date-input"
                  value={dateRange.start}
                  min={dataDateRange?.min}
                  max={dateRange.end || dataDateRange?.max}
                  onChange={e => { setDateRange(r => ({ ...r, start: e.target.value })); setHighlightedHaul(null); }}
                />
                <span className="date-sep">—</span>
                <input
                  type="date"
                  className="date-input"
                  value={dateRange.end}
                  min={dateRange.start || dataDateRange?.min}
                  max={dataDateRange?.max}
                  onChange={e => { setDateRange(r => ({ ...r, end: e.target.value })); setHighlightedHaul(null); }}
                />
              </div>
            </div>

            {tankData && activePage === 'chart' && (
              <>
                <TankChart
                  timeSeries={filteredTs}
                  hauls={filteredHauls}
                  bblPerIn={bblPerIn}
                  highlightedHaul={highlightedHaul}
                  onHaulClick={setHighlightedHaul}
                />

                <HaulTable
                  hauls={filteredHauls}
                  bblPerIn={bblPerIn}
                  highlightedHaul={highlightedHaul}
                  onHaulClick={setHighlightedHaul}
                  showDeactivated={showDeactivated}
                  onToggleShowDeactivated={() => setShowDeactivated(s => !s)}
                  onToggleHaul={(h) => deact.toggleHaul(haulKey(selectedWellId, activeTank, h))}
                />
              </>
            )}

            {tankData && activePage === 'data' && (
              <DataPage
                timeSeries={filteredTs}
                hauls={filteredHauls}
                wellName={wellData.wellName}
                tankLabel={activeTank}
                bblPerIn={bblPerIn}
              />
            )}

            {tankData && activePage === 'errors' && (
              <ErrorPlot
                hauls={tankData.hauls}
                bblPerIn={bblPerIn}
                numTanks={numTanks}
                wellName={wellData.wellName}
                fullLoaded={fullLoaded}
                onReviewHaul={reviewHaulOnChart}
              />
            )}

            {tankData && activePage === 'voldist' && (
              <VolumeDistribution
                hauls={tankData.hauls}
                bblPerIn={bblPerIn}
                numTanks={numTanks}
                wellName={wellData.wellName}
              />
            )}

            {tankData && activePage === 'leaks' && (
              <LeakDetector
                hauls={tankData.hauls}
                wellName={wellData.wellName}
                onReviewHaul={reviewHaulOnChart}
              />
            )}

            {tankData && activePage === 'waterrate' && (
              <WaterRatePage
                hauls={filteredHauls}
                wellName={wellData.wellName}
                tankLabel={activeTank}
                bblPerIn={bblPerIn}
              />
            )}
          </>
        )}
        {!wellLoading && !wellData && !loading && !selectedWellId && (
          <div className="no-data">Select a well from the sidebar</div>
        )}
        {!wellLoading && !wellData && !loading && selectedWellId && (
          <div className="loading"><div className="loading-spinner" />Loading well data...</div>
        )}
        </>
        )}
      </div>
    </div>
  );
}

export default App;
